import React, { useEffect, useMemo, useState } from "react";
import { fetchReportsByProducer } from "@/ReactInit/Scripts/DashboardPage/api/dashboardApi";
import { parseDashboardDate } from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";
import type {
  Producer,
  ProducerDetails,
  ReportSummary,
} from "@/ReactInit/Scripts/DashboardPage/types";
import { DashboardCard } from "@/ReactInit/Scripts/DashboardPage/components/DashboardCard";
import { DashboardButton } from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import { DashboardAlertBox } from "@/ReactInit/Scripts/DashboardPage/components/DashboardAlertBox";
import { DashboardConfirmationModal } from "@/ReactInit/Scripts/DashboardPage/components/DashboardConfirmationModal";
import {
  formatNorwegianPhone,
  formatOrgnr,
  norwegianNationalPhone,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";
import {
  getProducerById,
  sendInvite,
  setPhoneActivation,
} from "@/ReactInit/Scripts/DashboardPage/api/dashboardApi";
import DashboardPopover from "@/ReactInit/Scripts/DashboardPage/components/DashboardPopover";

interface DashboardProducerDetailsViewProps {
  producer: Producer | null;
  onBack: () => void;
  onEditProducer?: (producerId: string) => void;
  onCreateReport?: (producerId: string) => void;
  onProducerDeleted?: (producerId: string) => void;
  draftReportId?: string | null;
  onOpenDraftReport?: (reportId: string) => void;
}

const DashboardProducerDetailsView = ({
  producer,
  onBack,
  onEditProducer,
  onCreateReport,
  onProducerDeleted,
  draftReportId = null,
  onOpenDraftReport,
}: DashboardProducerDetailsViewProps) => {
  const [producerDetails, setProducerDetails] =
    useState<ProducerDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [draftReportIdLocal, setDraftReportIdLocal] = useState<string | null>(
    null
  );
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [producerReports, setProducerReports] = useState<ReportSummary[]>([]);

  const [actionError, setActionError] = useState<string | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteSuccess, setIsDeleteSuccess] = useState(false);

  const [inviteSuccessByPhoneId, setInviteSuccessByPhoneId] = useState<
    Record<string, boolean>
  >({});
  const [inviteLoadingByPhoneId, setInviteLoadingByPhoneId] = useState<
    Record<string, boolean>
  >({});

  const [resendLoadingByPhoneId, setResendLoadingByPhoneId] = useState<
    Record<string, boolean>
  >({});
  const [resendSuccessByPhoneId, setResendSuccessByPhoneId] = useState<
    Record<string, boolean>
  >({});

  const userRows = useMemo(() => {
    const rows = producerDetails?.phoneNumbers ?? [];
    return rows.filter((r) => !r.deactivated);
  }, [producerDetails]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!producer?.id) {
        setProducerDetails(null);
        return;
      }

      try {
        setIsLoadingDetails(true);
        setActionError(null);

        const full = await getProducerById(producer.id);
        if (!cancelled) setProducerDetails(full);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setProducerDetails(null);
          setActionError(
            e instanceof Error
              ? e.message
              : "Kunne ikke hente virksomhetsdetaljer."
          );
        }
      } finally {
        if (!cancelled) setIsLoadingDetails(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [producer?.id]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!producer?.id) {
        setDraftReportIdLocal(null);
        setProducerReports([]);
        return;
      }

      try {
        setIsLoadingDraft(true);

        const list = await fetchReportsByProducer(producer.id);

        if (cancelled) return;

        setProducerReports(list);

        const isDraft = (r: ReportSummary) => !parseDashboardDate(r.reportDate);

        const draft = list
          .filter((r) => isDraft(r))
          .sort((a, b) => b.year - a.year)[0];

        setDraftReportIdLocal(draft?.id ?? null);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setDraftReportIdLocal(null);
          setProducerReports([]);
        }
      } finally {
        if (!cancelled) setIsLoadingDraft(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [producer?.id]);

  if (!producer) {
    return (
      <div className="flex flex-col gap-4">
        <DashboardAlertBox type="info" label="Fant ingen virksomhet." />
        <div className="flex flex-row justify-center gap-3 mt-4">
          <DashboardButton
            label="Gå tilbake"
            variant="underline"
            icon="icon-arrow-left"
            onClick={onBack}
            scrollToId="dashboard"
            scrollOptions={{ durationMs: 400, easing: "easeInOutCubic" }}
          />
        </div>
      </div>
    );
  }

  const effectiveDraftReportId = draftReportIdLocal ?? draftReportId ?? null;
  const previousReports = producerReports
    .filter((r) => r.id !== effectiveDraftReportId)
    .sort((a, b) => b.year - a.year);

  const refreshDetails = async (producerId: string) => {
    const full = await getProducerById(producerId);
    setProducerDetails(full);
    setInviteSuccessByPhoneId({});
    setResendSuccessByPhoneId({});
  };

  const handleEdit = () => {
    setActionError(null);
    onEditProducer?.(producer.id);
  };

  const handleCreateReport = () => {
    setActionError(null);
    onCreateReport?.(producer.id);
  };

  const handleDeleteClick = () => {
    setActionError(null);
    setIsDeleteSuccess(false);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setIsDeleteSuccess(false);
  };

  const handleConfirmDelete = async () => {
    if (!producer?.id) return;

    try {
      setIsDeleting(true);
      setActionError(null);

      const full = await getProducerById(producer.id);

      const phoneIdsToDeactivate = (full.phoneNumbers ?? [])
        .map((p) => p.phoneId?.trim() ?? "")
        .filter(Boolean)
        .filter((phoneId) => {
          const row = full.phoneNumbers.find(
            (x) => (x.phoneId?.trim() ?? "") === phoneId
          );
          return row ? !row.deactivated : true;
        });

      if (phoneIdsToDeactivate.length > 0) {
        await Promise.all(
          phoneIdsToDeactivate.map((phoneId) =>
            setPhoneActivation({
              producerId: producer.id,
              phoneId,
              deactivated: true,
            })
          )
        );
      }

      setIsDeleteSuccess(true);
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved sletting av virksomheten."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteSuccessClose = () => {
    setIsDeleteModalOpen(false);
    setIsDeleteSuccess(false);
    onProducerDeleted?.(producer.id);
    onBack();
  };

  const getPhoneRow = (phoneId: string) =>
    producerDetails?.phoneNumbers?.find(
      (p) => (p.phoneId?.trim() || "") === phoneId
    ) ?? null;

  const guardNotDeactivated = (phoneId: string): boolean => {
    const row = getPhoneRow(phoneId);
    if (!row) {
      setActionError("Fant ikke brukeren. Prøv å laste siden på nytt.");
      return false;
    }
    if (row.deactivated) {
      setActionError("Brukeren er deaktivert og kan ikke inviteres på nytt.");
      return false;
    }
    return true;
  };

  const handleSendInvite = async (phoneId: string) => {
    if (!producer?.id) return;
    if (!guardNotDeactivated(phoneId)) return;

    try {
      setActionError(null);
      setInviteSuccessByPhoneId((p) => ({ ...p, [phoneId]: false }));
      setInviteLoadingByPhoneId((p) => ({ ...p, [phoneId]: true }));

      await sendInvite({ producerId: producer.id, phoneId });

      setInviteSuccessByPhoneId((p) => ({ ...p, [phoneId]: true }));

      await refreshDetails(producer.id);
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error ? e.message : "Kunne ikke sende invitasjon."
      );
    } finally {
      setInviteLoadingByPhoneId((p) => ({ ...p, [phoneId]: false }));
    }
  };

  const handleResendInvite = async (phoneId: string) => {
    if (!producer?.id) return;
    if (!guardNotDeactivated(phoneId)) return;

    try {
      setActionError(null);
      setResendSuccessByPhoneId((p) => ({ ...p, [phoneId]: false }));
      setResendLoadingByPhoneId((p) => ({ ...p, [phoneId]: true }));

      await setPhoneActivation({
        producerId: producer.id,
        phoneId,
        isInvited: false,
        deactivated: false,
      });

      await sendInvite({ producerId: producer.id, phoneId });

      setResendSuccessByPhoneId((p) => ({ ...p, [phoneId]: true }));
      await refreshDetails(producer.id);
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error ? e.message : "Kunne ikke sende ny invitasjon."
      );
    } finally {
      setResendLoadingByPhoneId((p) => ({ ...p, [phoneId]: false }));
    }
  };

  return (
    <div className="flex flex-col gap-5 w-full mx-auto max-w-[932px]">
      {actionError && <DashboardAlertBox type="error" label={actionError} />}
      {isLoadingDetails && (
        <DashboardAlertBox type="info" label="Laster brukerinformasjon…" />
      )}
      {effectiveDraftReportId && !isLoadingDetails && !isLoadingDraft && (
        <DashboardAlertBox
          type="warning"
          label={`${producer.name} har en foreløpig rapport som ikke er fullført ennå.`}
        />
      )}
      <DashboardCard as="section">
        <div className="flex flex-col gap-5 tablet:gap-10">
          <div className="flex flex-col gap-5">
            <h2 className="text-3xl font-semibold">Virksomhetsinformasjon</h2>
            <div className="grid text-base font-semibold grid-cols-2 gap-x-5 gap-y-6">
              <div className="flex flex-col gap-3">
                <p className="text-black-subtile">Virksomhet</p>
                <p>{producer.name ?? "–"}</p>
              </div>

              <div className="flex flex-col gap-3 truncate">
                <p className="text-black-subtile">Organisasjonsnummer</p>
                <p>{formatOrgnr(producer.orgnr)}</p>
              </div>
            </div>
          </div>
          <hr className="border-t border-black-muted" />
          <div className="flex flex-col gap-5">
            <h2 className="text-3xl font-semibold">Brukerinformasjon</h2>

            {userRows.length === 0 ? (
              <p className="text-sm text-black-subtile">
                Ingen brukere registrert på virksomheten.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="dashboardTable reportTable">
                  <thead>
                    <tr>
                      <th>Brukernavn</th>
                      <th>Telefonnummer</th>
                      <th>E-post</th>
                      <th>Handling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userRows.map((ph) => {
                      const phoneId = ph.phoneId?.trim() || "";
                      const isDeactivated = ph.deactivated;
                      const canInvite =
                        !!phoneId && !ph.isInvited && !isDeactivated;
                      const isLoading =
                        !!phoneId && inviteLoadingByPhoneId[phoneId];
                      const inviteOk =
                        !!phoneId && inviteSuccessByPhoneId[phoneId];
                      const isResending =
                        !!phoneId && resendLoadingByPhoneId[phoneId];
                      const resendOk =
                        !!phoneId && resendSuccessByPhoneId[phoneId];
                      const invitedLabel = resendOk
                        ? "Ny invitasjon sendt!"
                        : "Invitert";

                      return (
                        <tr
                          key={
                            ph.phoneId ??
                            `${producer.id}-${ph.phoneNumber ?? ""}`
                          }
                        >
                          <td>{ph.name || "(Uten navn)"}</td>
                          <td>
                            {formatNorwegianPhone(
                              norwegianNationalPhone(ph.phoneNumber || "")
                            ) || "–"}
                          </td>
                          <td
                            className="max-w-56 truncate"
                            title={ph.email?.trim() || "-"}
                          >
                            {ph.email?.trim() || "–"}
                          </td>

                          <td className="min-w-[180px]">
                            {canInvite ? (
                              <DashboardButton
                                label={inviteOk ? "Sendt!" : "Send invitasjon"}
                                labelClass="text-base"
                                variant="underline"
                                icon="icon-send"
                                onClick={() => void handleSendInvite(phoneId)}
                                disabled={isLoading}
                                loading={isLoading}
                                loadingText="Sender…"
                                noPadding
                              />
                            ) : ph.isInvited ? (
                              <div className="w-fit">
                                <DashboardPopover
                                  variant="text"
                                  label={invitedLabel}
                                  icon="icon-mail-check"
                                  labelClass="text-base"
                                  loading={isResending}
                                  loadingLabel="Sender ny invitasjon…"
                                  disabled={
                                    !phoneId ||
                                    isLoading ||
                                    isResending ||
                                    isDeactivated
                                  }
                                  items={[
                                    {
                                      label: "Send invitasjon på nytt",
                                      value: "resend",
                                    },
                                  ]}
                                  onSelect={(action) => {
                                    if (!phoneId) return;
                                    if (action === "resend") {
                                      void handleResendInvite(phoneId);
                                    }
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-sm text-black-subtile">
                                –
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex flex-col tablet:flex-row justify-center gap-3">
            <DashboardButton
              label="Rediger"
              variant="underline"
              icon="icon-square-pen"
              onClick={handleEdit}
            />
            {effectiveDraftReportId ? (
              <>
                <DashboardButton
                  label="Foreløpig rapport"
                  variant="underline"
                  icon="icon-file-pen"
                  onClick={() => {
                    setActionError(null);
                    onOpenDraftReport?.(effectiveDraftReportId);
                  }}
                />
                <DashboardButton
                  label="Lag rapport"
                  variant="underline"
                  icon="icon-file-plus"
                  onClick={handleCreateReport}
                />
              </>
            ) : (
              <DashboardButton
                label="Lag rapport"
                variant="underline"
                icon="icon-file-plus"
                onClick={handleCreateReport}
              />
            )}
            <DashboardButton
              label="Slett virksomhet"
              variant="underline"
              icon="icon-trash"
              onClick={handleDeleteClick}
            />
          </div>
        </div>
      </DashboardCard>
      <div className="flex flex-row justify-center gap-3 mt-4">
        <DashboardButton
          label="Gå tilbake"
          variant="darkplain"
          onClick={onBack}
          icon="icon-arrow-left"
          scrollToId="dashboard"
          scrollOptions={{ durationMs: 400, easing: "easeInOutCubic" }}
        />
      </div>
      {!!onOpenDraftReport && previousReports.length > 0 && (
        <div className="flex flex-col gap-4 mt-10">
          <h2 className="text-3xl font-semibold">Tidligere rapporter</h2>
          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-2">
            {previousReports.map((r) => (
              <DashboardCard
                className="flex flex-col gap-3"
                size="slim"
                key={r.id}
              >
                <div className="text-lg leading-6 font-semibold truncate">
                  {r.year}-rapport
                </div>
                <DashboardButton
                  label="Gå til rapporten"
                  variant="underline"
                  icon="icon-arrow-right"
                  iconPosition="right"
                  onClick={() => {
                    setActionError(null);
                    onOpenDraftReport(r.id);
                  }}
                />
              </DashboardCard>
            ))}
          </div>
        </div>
      )}
      <DashboardConfirmationModal
        isOpen={isDeleteModalOpen}
        type="alert"
        title="Er du sikker på at du vil slette virksomheten?"
        description="Ved å slette virksomheten mister du all tilknyttet informasjon. Handlingen kan ikke angres."
        confirmLabel={isDeleting ? "Sletter…" : "Ja, slett virksomheten"}
        cancelLabel="Gå tilbake"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmDisabled={isDeleting}
        cancelDisabled={isDeleting}
        isSuccess={isDeleteSuccess}
        successTitle="Virksomheten er slettet"
        successButtonLabel="Ok"
        onSuccessClose={handleDeleteSuccessClose}
      />
    </div>
  );
};

export default DashboardProducerDetailsView;
